import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import withReduxProvider from "web/react/redux-provider";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import userProfiler from "web/script/modules/userprofiler";
import storage from "web/script/utils/storage";

interface AspirerAcquisitionCampaignControlProps {
    isVisible: boolean;
    timeout?: number;
}

const ASPIRER_MODAL_KEY = "AspirerCampaignTimestamp";

/** Finds if the aspirer campaign should be active or not.
 *
 * Retrieves the last timestamp from localStorage for this campaign, if possible,
 * and calculates if it is over a threshold (daysBeforeShowingAgain). If so,
 * or if the localStorage is null, or if the value for the key is not coercible
 * into a timestamp, then set the localStorage timestamp to now and return true.
 *
 * Otherwise return false, which will be used to not dispatch the event to trigger
 * the modal.
 */
function _campaignShouldBeActive(daysBeforeShowingAgain = 30): boolean {
    const storedValue = storage.get(ASPIRER_MODAL_KEY);
    const timestampFromStorage = Number(storedValue);
    const currentTimestamp = Date.now();

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const daysSinceLastShown = (currentTimestamp - timestampFromStorage) / millisecondsPerDay;

    if (
        !storedValue ||
        Number.isNaN(daysSinceLastShown) ||
        daysSinceLastShown >= daysBeforeShowingAgain
    ) {
        storage.set(ASPIRER_MODAL_KEY, currentTimestamp.toString());
        return true;
    }
    return false;
}

function _AspirerAcquisitionCampaignControl({
    isVisible,
    timeout = 800,
}: AspirerAcquisitionCampaignControlProps): JSX.Element {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);

    const { isDomLoaded } = useDomViewport();

    useEffect(() => {
        if (isDomLoaded) setShowModal(_campaignShouldBeActive());
    }, [isDomLoaded]);

    useEffect(() => {
        if (showModal && !userProfiler.isLoggedIn() && isVisible) {
            setTimeout(() => {
                dispatch(
                    openCustomerCaptureOverlay({
                        analyticsEventCategory: "aspirer_acquisition_campaign",
                        analyticsEventLabel: "aspirer_editorial_capsule",
                        captureType: "aspirer_acquisition_campaign",
                        componentType: "sign_up",
                        ctaKey: "general.continue",
                        showSocialAuth: false,
                        imageSrc:
                            "https://cdna.lystit.com/static/n/static-img/sign-up-modal/editorial-modal-image.jpg",
                        legalTextKey:
                            "account.register.legal_agreement.sign_up_and_subscribe_terms",
                    })
                );
            }, timeout);
        }
    }, [dispatch, isVisible, showModal, timeout]);

    return <div />;
}

export const AspirerAcquisitionCampaignControl = withReduxProvider(
    _AspirerAcquisitionCampaignControl
);
